import { useState, useRef } from "react";
import PropTypes from "prop-types";

import Button from "@shared/v2/Button";
import Checkbox from "@shared/v2/Checkbox";

import { DndContext, useSensor, useSensors, PointerSensor, KeyboardSensor } from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Grip as GripIcon, AngleDownIcon } from "../../Transactions/components/icons";
import useOutsideClick from "../../Transactions/utils/useOnClickOutside";

const ColumnOption = ({ label = null, checked = false, id = null, onChange = null, fixed = false }) => {
  const labelColor = fixed ? "tw-text-gray" : "tw-text-gray-75";
  const gripColor = fixed ? "tw-invisible" : "tw-text-gray tw-cursor-move";

  return (
    <div
      key={`${id}-${checked}`}
      className="tw-flex tw-bg-white tw-flex-row tw-space-x-2 tw-p-10px tw-items-center tw-justify-between tw-min-w-full"
    >
      <div className="tw-flex tw-flex-row tw-space-x-2 tw-items-center">
        <Checkbox onChange={fixed ? () => {} : onChange} checked={checked} />
        <span className={labelColor}>{label}</span>
      </div>
      <div>
        <span className={`${gripColor}`}>
          <GripIcon />
        </span>
      </div>
    </div>
  );
};

const SortableOption = ({ option = null, onChange = null }) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: option.id,
    disabled: !!option.fixed,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <ColumnOption
        label={option.label}
        id={option.id}
        onChange={() => onChange(option.id)}
        checked={option.active}
        fixed={!!option.fixed}
      />
    </div>
  );
};

const ColumnsDropdown = ({ options = [], onChange = null, onDrag = null }) => {
  const [opened, setOpened] = useState(false);
  const ref = useRef();
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor),
  );

  useOutsideClick(ref, () => setOpened(false));

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIndex = options.findIndex((o) => o.id === active.id);
    const newIndex = options.findIndex((o) => o.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    if (options[newIndex].fixed) return;

    onDrag(oldIndex, newIndex);
  };

  return (
    <div ref={ref} className="tw-relative tw-z-30">
      <Button schema="secondary" size="small" onClick={() => setOpened(!opened)}>
        <div className="tw-flex tw-items-center tw-space-x-2 tw-font-bold">
          <span className="tw-inline-flex tw-items-center">COLUMNS</span>
          <AngleDownIcon />
        </div>
      </Button>
      {opened && (
        <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
          <SortableContext items={options.map((o) => o.id)} strategy={verticalListSortingStrategy}>
            <div
              role="presentation"
              className="tw-absolute tw-bg-white tw-min-w-96px tw-w-196px tw-border-solid tw-rounded-4px tw-border-2 tw-border-gray-10 tw-max-h-[600px] tw-overflow-y-auto"
              onClick={(e) => e.nativeEvent.stopImmediatePropagation()}
            >
              {options.map((option) => (
                <SortableOption key={option.id} option={option} onChange={onChange} />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
};

ColumnOption.propTypes = {
  label: PropTypes.string,
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  checked: PropTypes.bool,
  fixed: PropTypes.bool,
  onChange: PropTypes.func,
};

SortableOption.propTypes = {
  option: PropTypes.shape({
    label: PropTypes.string,
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    active: PropTypes.bool,
    fixed: PropTypes.bool,
  }),
  onChange: PropTypes.func,
};

ColumnsDropdown.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      fixed: PropTypes.bool,
    }),
  ),
  onChange: PropTypes.func,
  onDrag: PropTypes.func,
};

export default ColumnsDropdown;
